import dayjs from 'dayjs';
import {throttle} from 'lodash';
import {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {Typography} from '@/components/Typography';
import {TransparentBackdrop} from '@/styled-components';
import {palette, Z_INDEX} from '@/styles';
import IconClose from './icon-24-searchclose.svg';
import {MiniMap} from './MiniMap';

interface IProps {
  position?: naver.maps.LatLng;
  onPositionChanged?(position: naver.maps.LatLng): void;
  onPovChanged?(pov: naver.maps.PanoramaPov): void;
  onClose?(): void;
}

const PANORAMA_DOM_ID = 'myfc--panarama-map-container';

export const Panorama = ({
  position,
  onPositionChanged,
  onPovChanged,
  onClose,
}: IProps) => {
  const panoRef = useRef<naver.maps.Panorama>();
  const [rotate, setRotate] = useState<number>(0);
  const [center, setCenter] = useState<naver.maps.LatLng | undefined>(position);
  const [address, setAddress] = useState<string>();
  const [photodate, setPhotoDate] = useState<string>();
  const [loaded, setLoaded] = useState<boolean>(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResize = useCallback(
    throttle(() => {
      if (panoRef.current) {
        const wh = window.innerHeight;
        const ww = window.innerWidth;

        panoRef.current.setSize(new naver.maps.Size(ww, wh));
      }
    }, 500),
    []
  );

  const handlePanoChanged = useCallback(() => {
    if (!panoRef?.current) {
      return;
    }

    const pos = panoRef.current.getPosition();
    const loc = panoRef.current.getLocation();

    onPositionChanged?.(pos);
    setCenter(pos);
    setAddress(loc?.address);
    setPhotoDate(loc?.photodate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panoRef.current]);

  const handlePovChanged = useCallback(() => {
    if (!panoRef.current) {
      return;
    }

    const pov = panoRef.current.getPov();

    if (pov) {
      setRotate(pov.pan ?? 0);
      onPovChanged?.(pov);
    }
  }, [onPovChanged]);

  const onPointChanged = useCallback((pos: naver.maps.LatLng) => {
    if (!panoRef?.current) {
      return;
    }

    panoRef.current.setPosition(pos);
  }, []);

  useEffect(() => {
    if (position) {
      const pano = new naver.maps.Panorama(PANORAMA_DOM_ID, {
        position,
      });

      panoRef.current = pano;

      naver.maps.Event.addListener(pano, 'init', () => setLoaded(true));
      naver.maps.Event.addListener(pano, 'pano_changed', handlePanoChanged);
      naver.maps.Event.addListener(pano, 'pov_changed', handlePovChanged);

      window.removeEventListener('resize', handleResize);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Root>
      <div id={PANORAMA_DOM_ID} />
      <Close onClick={onClose} />
      {loaded && (
        <MiniMap center={center} rotate={rotate} onClick={onPointChanged} />
      )}
      {!!(address?.length || photodate?.length) && (
        <Infomation>
          {!!address?.length && (
            <Typography variant="medium14" fontWeight="700">
              {address}
            </Typography>
          )}
          {!!photodate?.length && (
            <Typography variant="medium14">
              &nbsp;{dayjs(photodate).format('YYYY년 M월 D일')}
            </Typography>
          )}
        </Infomation>
      )}
    </Root>
  );
};

const Root = styled(TransparentBackdrop)`
  z-index: ${Z_INDEX.globalFloating - 10};
  background-color: ${palette.gray500};
  #${PANORAMA_DOM_ID} {
    width: 100%;
    height: 100%;
  }
`;

const Close = styled.div`
  position: fixed;
  z-index: ${Z_INDEX.globalFloating - 9};
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background-color: ${palette.gray800};
  right: 24px;
  top: 24px;
  background-image: url(${IconClose});
  background-position: center;
  background-repeat: no-repeat;
  cursor: pointer;
`;

const Infomation = styled.div`
  position: fixed;
  left: 24px;
  top: 24px;
  z-index: ${Z_INDEX.globalFloating - 9};
  background-color: ${palette.gray800};
  border-radius: 4px;
  padding: 4px 8px;
  color: ${palette.white};
`;
